// 📋 Global State Management with Context API
// Advanced React Pattern: Global State with Context + useReducer

import React, { createContext, useContext, useReducer, useCallback, useMemo, useEffect } from 'react';
import {
  canAccessMenu as aclCanAccessMenu,
  normalizeAdminUser,
  resolveUserPermissions,
} from '../security/acl';
import {
  createNotificationId,
  loadStoredNotifications,
  saveStoredNotifications,
} from './notificationStorage';

// 🎯 Initial State (Normalized Data Structure)
const initialState = {
  // Dashboard data
  dashboard: {
    stats: {
      totalBookings: 0,
      totalRevenue: 0,
      activeVendors: 0,
      pendingBookings: 0,
      loading: false,
      error: null,
    },
    recentBookings: [],
    revenueChart: [],
  },

  // Bookings data (Normalized structure)
  bookings: {
    byId: {},
    allIds: [],
    filters: {
      status: 'all',
      dateRange: null,
      vendor: null,
    },
    pagination: {
      page: 1,
      limit: 10,
      total: 0,
    },
    loading: false,
    error: null,
  },

  // Vendors data
  vendors: {
    byId: {},
    allIds: [],
    filters: {
      status: 'all',
      location: null,
    },
    loading: false,
    error: null,
  },

  // Users data
  users: {
    byId: {},
    allIds: [],
    currentUser: normalizeAdminUser(null),
    permissions: ['*'],
    loading: false,
    error: null,
  },

  // UI State
  ui: {
    sidebar: {
      isOpen: true,
      activeMenu: 'dashboard',
    },
    theme: 'light',
    notifications: loadStoredNotifications(),
    modal: {
      isOpen: false,
      type: null,
      data: null,
    },
    loading: {
      global: false,
      components: {},
    },
  },

  // Cache for API responses
  cache: {
    timestamp: Date.now(),
    ttl: 5 * 60 * 1000, // 5 minutes
    data: {},
  },
};

// 🎬 Action Types (Using constants to prevent typos)
export const ACTION_TYPES = {
  // Dashboard Actions
  SET_DASHBOARD_LOADING: 'SET_DASHBOARD_LOADING',
  SET_DASHBOARD_STATS: 'SET_DASHBOARD_STATS',
  SET_DASHBOARD_ERROR: 'SET_DASHBOARD_ERROR',

  // Booking Actions
  SET_BOOKINGS_LOADING: 'SET_BOOKINGS_LOADING',
  SET_BOOKINGS: 'SET_BOOKINGS',
  ADD_BOOKING: 'ADD_BOOKING',
  UPDATE_BOOKING: 'UPDATE_BOOKING',
  DELETE_BOOKING: 'DELETE_BOOKING',
  SET_BOOKING_FILTERS: 'SET_BOOKING_FILTERS',
  SET_BOOKING_PAGINATION: 'SET_BOOKING_PAGINATION',
  SET_BOOKINGS_ERROR: 'SET_BOOKINGS_ERROR',

  // Vendor Actions
  SET_VENDORS_LOADING: 'SET_VENDORS_LOADING',
  SET_VENDORS: 'SET_VENDORS',
  ADD_VENDOR: 'ADD_VENDOR',
  UPDATE_VENDOR: 'UPDATE_VENDOR',
  DELETE_VENDOR: 'DELETE_VENDOR',
  SET_VENDOR_FILTERS: 'SET_VENDOR_FILTERS',
  SET_VENDORS_ERROR: 'SET_VENDORS_ERROR',

  // User Actions
  SET_USERS_LOADING: 'SET_USERS_LOADING',
  SET_USERS: 'SET_USERS',
  SET_CURRENT_USER: 'SET_CURRENT_USER',
  SET_USER_PERMISSIONS: 'SET_USER_PERMISSIONS',
  SET_USERS_ERROR: 'SET_USERS_ERROR',

  // UI Actions
  TOGGLE_SIDEBAR: 'TOGGLE_SIDEBAR',
  SET_ACTIVE_MENU: 'SET_ACTIVE_MENU',
  SET_THEME: 'SET_THEME',
  ADD_NOTIFICATION: 'ADD_NOTIFICATION',
  REMOVE_NOTIFICATION: 'REMOVE_NOTIFICATION',
  MARK_NOTIFICATION_READ: 'MARK_NOTIFICATION_READ',
  MARK_ALL_NOTIFICATIONS_READ: 'MARK_ALL_NOTIFICATIONS_READ',
  CLEAR_NOTIFICATIONS: 'CLEAR_NOTIFICATIONS',
  HYDRATE_NOTIFICATIONS: 'HYDRATE_NOTIFICATIONS',
  OPEN_MODAL: 'OPEN_MODAL',
  CLOSE_MODAL: 'CLOSE_MODAL',
  SET_COMPONENT_LOADING: 'SET_COMPONENT_LOADING',
  SET_GLOBAL_LOADING: 'SET_GLOBAL_LOADING',

  // Cache Actions
  SET_CACHE: 'SET_CACHE',
  CLEAR_CACHE: 'CLEAR_CACHE',
};

// 🎭 Reducer Function (Pure function for state updates)
const appReducer = (state, action) => {
  switch (action.type) {
    // Dashboard Reducers
    case ACTION_TYPES.SET_DASHBOARD_LOADING:
      return {
        ...state,
        dashboard: {
          ...state.dashboard,
          stats: {
            ...state.dashboard.stats,
            loading: action.payload,
          },
        },
      };

    case ACTION_TYPES.SET_DASHBOARD_STATS:
      return {
        ...state,
        dashboard: {
          ...state.dashboard,
          stats: {
            ...state.dashboard.stats,
            ...action.payload,
            loading: false,
            error: null,
          },
        },
      };

    case ACTION_TYPES.SET_DASHBOARD_ERROR:
      return {
        ...state,
        dashboard: {
          ...state.dashboard,
          stats: {
            ...state.dashboard.stats,
            loading: false,
            error: action.payload,
          },
        },
      };

    // Booking Reducers (Normalized state update)
    case ACTION_TYPES.SET_BOOKINGS_LOADING:
      return {
        ...state,
        bookings: {
          ...state.bookings,
          loading: action.payload,
        },
      };

    case ACTION_TYPES.SET_BOOKINGS:
      // Normalize bookings data
      const bookingsById = {};
      const bookingIds = action.payload.map(booking => {
        bookingsById[booking.id] = booking;
        return booking.id;
      });

      return {
        ...state,
        bookings: {
          ...state.bookings,
          byId: bookingsById,
          allIds: bookingIds,
          loading: false,
          error: null,
        },
      };

    case ACTION_TYPES.ADD_BOOKING:
      return {
        ...state,
        bookings: {
          ...state.bookings,
          byId: {
            ...state.bookings.byId,
            [action.payload.id]: action.payload,
          },
          allIds: [action.payload.id, ...state.bookings.allIds],
        },
      };

    case ACTION_TYPES.UPDATE_BOOKING:
      return {
        ...state,
        bookings: {
          ...state.bookings,
          byId: {
            ...state.bookings.byId,
            [action.payload.id]: {
              ...state.bookings.byId[action.payload.id],
              ...action.payload,
            },
          },
        },
      };

    case ACTION_TYPES.DELETE_BOOKING:
      const { [action.payload]: deletedBooking, ...remainingBookings } = state.bookings.byId;
      return {
        ...state,
        bookings: {
          ...state.bookings,
          byId: remainingBookings,
          allIds: state.bookings.allIds.filter(id => id !== action.payload),
        },
      };

    case ACTION_TYPES.SET_BOOKING_FILTERS:
      return {
        ...state,
        bookings: {
          ...state.bookings,
          filters: {
            ...state.bookings.filters,
            ...action.payload,
          },
        },
      };

    case ACTION_TYPES.SET_BOOKING_PAGINATION:
      return {
        ...state,
        bookings: {
          ...state.bookings,
          pagination: {
            ...state.bookings.pagination,
            ...action.payload,
          },
        },
      };

    case ACTION_TYPES.SET_BOOKINGS_ERROR:
      return {
        ...state,
        bookings: {
          ...state.bookings,
          loading: false,
          error: action.payload,
        },
      };

    // Vendor Reducers
    case ACTION_TYPES.SET_VENDORS_LOADING:
      return {
        ...state,
        vendors: {
          ...state.vendors,
          loading: action.payload,
        },
      };

    // User Reducers
    case ACTION_TYPES.SET_CURRENT_USER:
      return {
        ...state,
        users: {
          ...state.users,
          currentUser: normalizeAdminUser(action.payload),
          permissions: resolveUserPermissions(action.payload),
        },
      };

    case ACTION_TYPES.SET_USER_PERMISSIONS:
      return {
        ...state,
        users: {
          ...state.users,
          permissions: Array.isArray(action.payload) ? action.payload : [],
        },
      };

    case ACTION_TYPES.SET_VENDORS:
      const vendorsById = {};
      const vendorIds = action.payload.map(vendor => {
        vendorsById[vendor.id] = vendor;
        return vendor.id;
      });

      return {
        ...state,
        vendors: {
          ...state.vendors,
          byId: vendorsById,
          allIds: vendorIds,
          loading: false,
          error: null,
        },
      };

    // UI Reducers
    case ACTION_TYPES.TOGGLE_SIDEBAR:
      return {
        ...state,
        ui: {
          ...state.ui,
          sidebar: {
            ...state.ui.sidebar,
            isOpen: !state.ui.sidebar.isOpen,
          },
        },
      };

    case ACTION_TYPES.SET_ACTIVE_MENU:
      return {
        ...state,
        ui: {
          ...state.ui,
          sidebar: {
            ...state.ui.sidebar,
            activeMenu: action.payload,
          },
        },
      };

    case ACTION_TYPES.SET_THEME:
      return {
        ...state,
        ui: {
          ...state.ui,
          theme: action.payload,
        },
      };

    case ACTION_TYPES.HYDRATE_NOTIFICATIONS:
      return {
        ...state,
        ui: {
          ...state.ui,
          notifications: Array.isArray(action.payload) ? action.payload : [],
        },
      };

    case ACTION_TYPES.ADD_NOTIFICATION: {
      const entry = {
        id: createNotificationId(),
        timestamp: new Date().toISOString(),
        read: false,
        ...action.payload,
      };
      const notifications = [...state.ui.notifications, entry];
      saveStoredNotifications(notifications);
      return {
        ...state,
        ui: {
          ...state.ui,
          notifications,
        },
      };
    }

    case ACTION_TYPES.REMOVE_NOTIFICATION: {
      const notifications = state.ui.notifications.filter(
        (notification) => notification.id !== action.payload,
      );
      saveStoredNotifications(notifications);
      return {
        ...state,
        ui: {
          ...state.ui,
          notifications,
        },
      };
    }

    case ACTION_TYPES.MARK_NOTIFICATION_READ: {
      const notifications = state.ui.notifications.map((notification) =>
        notification.id === action.payload
          ? { ...notification, read: true }
          : notification,
      );
      saveStoredNotifications(notifications);
      return {
        ...state,
        ui: {
          ...state.ui,
          notifications,
        },
      };
    }

    case ACTION_TYPES.MARK_ALL_NOTIFICATIONS_READ: {
      const notifications = state.ui.notifications.map((notification) => ({
        ...notification,
        read: true,
      }));
      saveStoredNotifications(notifications);
      return {
        ...state,
        ui: {
          ...state.ui,
          notifications,
        },
      };
    }

    case ACTION_TYPES.CLEAR_NOTIFICATIONS:
      saveStoredNotifications([]);
      return {
        ...state,
        ui: {
          ...state.ui,
          notifications: [],
        },
      };

    case ACTION_TYPES.OPEN_MODAL:
      return {
        ...state,
        ui: {
          ...state.ui,
          modal: {
            isOpen: true,
            type: action.payload.type,
            data: action.payload.data,
          },
        },
      };

    case ACTION_TYPES.CLOSE_MODAL:
      return {
        ...state,
        ui: {
          ...state.ui,
          modal: {
            isOpen: false,
            type: null,
            data: null,
          },
        },
      };

    case ACTION_TYPES.SET_COMPONENT_LOADING:
      return {
        ...state,
        ui: {
          ...state.ui,
          loading: {
            ...state.ui.loading,
            components: {
              ...state.ui.loading.components,
              [action.payload.component]: action.payload.loading,
            },
          },
        },
      };

    case ACTION_TYPES.SET_GLOBAL_LOADING:
      return {
        ...state,
        ui: {
          ...state.ui,
          loading: {
            ...state.ui.loading,
            global: action.payload,
          },
        },
      };

    // Cache Reducers
    case ACTION_TYPES.SET_CACHE:
      return {
        ...state,
        cache: {
          ...state.cache,
          data: {
            ...state.cache.data,
            [action.payload.key]: {
              data: action.payload.data,
              timestamp: Date.now(),
            },
          },
        },
      };

    case ACTION_TYPES.CLEAR_CACHE:
      return {
        ...state,
        cache: {
          ...state.cache,
          data: {},
        },
      };

    default:
      return state;
  }
};

// 🏭 Context Creation
const AppContext = createContext(null);

// 🎪 Context Provider Component
export const AppProvider = ({ children }) => {
  const [state, dispatch] = useReducer(appReducer, initialState);

  useEffect(() => {
    dispatch({
      type: ACTION_TYPES.HYDRATE_NOTIFICATIONS,
      payload: loadStoredNotifications(),
    });
  }, []);

  // 🎯 Memoized Action Creators (Performance optimization)
  const actions = useMemo(() => ({
    // Dashboard actions
    setDashboardLoading: (loading) => dispatch({
      type: ACTION_TYPES.SET_DASHBOARD_LOADING,
      payload: loading,
    }),

    setDashboardStats: (stats) => dispatch({
      type: ACTION_TYPES.SET_DASHBOARD_STATS,
      payload: stats,
    }),

    setDashboardError: (error) => dispatch({
      type: ACTION_TYPES.SET_DASHBOARD_ERROR,
      payload: error,
    }),

    // Booking actions
    setBookingsLoading: (loading) => dispatch({
      type: ACTION_TYPES.SET_BOOKINGS_LOADING,
      payload: loading,
    }),

    setBookings: (bookings) => dispatch({
      type: ACTION_TYPES.SET_BOOKINGS,
      payload: bookings,
    }),

    addBooking: (booking) => dispatch({
      type: ACTION_TYPES.ADD_BOOKING,
      payload: booking,
    }),

    updateBooking: (booking) => dispatch({
      type: ACTION_TYPES.UPDATE_BOOKING,
      payload: booking,
    }),

    deleteBooking: (bookingId) => dispatch({
      type: ACTION_TYPES.DELETE_BOOKING,
      payload: bookingId,
    }),

    setBookingFilters: (filters) => dispatch({
      type: ACTION_TYPES.SET_BOOKING_FILTERS,
      payload: filters,
    }),

    setBookingPagination: (pagination) => dispatch({
      type: ACTION_TYPES.SET_BOOKING_PAGINATION,
      payload: pagination,
    }),

    // Vendor actions
    setVendorsLoading: (loading) => dispatch({
      type: ACTION_TYPES.SET_VENDORS_LOADING,
      payload: loading,
    }),

    setVendors: (vendors) => dispatch({
      type: ACTION_TYPES.SET_VENDORS,
      payload: vendors,
    }),

    setCurrentUser: (user) => dispatch({
      type: ACTION_TYPES.SET_CURRENT_USER,
      payload: user,
    }),

    setUserPermissions: (permissions) => dispatch({
      type: ACTION_TYPES.SET_USER_PERMISSIONS,
      payload: permissions,
    }),

    // UI actions
    toggleSidebar: () => dispatch({
      type: ACTION_TYPES.TOGGLE_SIDEBAR,
    }),

    setActiveMenu: (menu) => dispatch({
      type: ACTION_TYPES.SET_ACTIVE_MENU,
      payload: menu,
    }),

    setTheme: (theme) => dispatch({
      type: ACTION_TYPES.SET_THEME,
      payload: theme,
    }),

    addNotification: (notification) => dispatch({
      type: ACTION_TYPES.ADD_NOTIFICATION,
      payload: notification,
    }),

    removeNotification: (id) => dispatch({
      type: ACTION_TYPES.REMOVE_NOTIFICATION,
      payload: id,
    }),

    markNotificationRead: (id) => dispatch({
      type: ACTION_TYPES.MARK_NOTIFICATION_READ,
      payload: id,
    }),

    markAllNotificationsRead: () => dispatch({
      type: ACTION_TYPES.MARK_ALL_NOTIFICATIONS_READ,
    }),

    clearNotifications: () => dispatch({
      type: ACTION_TYPES.CLEAR_NOTIFICATIONS,
    }),

    openModal: (type, data) => dispatch({
      type: ACTION_TYPES.OPEN_MODAL,
      payload: { type, data },
    }),

    closeModal: () => dispatch({
      type: ACTION_TYPES.CLOSE_MODAL,
    }),

    setComponentLoading: (component, loading) => dispatch({
      type: ACTION_TYPES.SET_COMPONENT_LOADING,
      payload: { component, loading },
    }),

    setGlobalLoading: (loading) => dispatch({
      type: ACTION_TYPES.SET_GLOBAL_LOADING,
      payload: loading,
    }),

    // Cache actions
    setCache: (key, data) => dispatch({
      type: ACTION_TYPES.SET_CACHE,
      payload: { key, data },
    }),

    clearCache: () => dispatch({
      type: ACTION_TYPES.CLEAR_CACHE,
    }),
  }), []);

  // 🎯 Selectors (Derived state with memoization)
  const selectors = useMemo(() => ({
    // Dashboard selectors
    getDashboardStats: () => state.dashboard.stats,

    // Booking selectors
    getAllBookings: () => state.bookings.allIds.map(id => state.bookings.byId[id]),
    getFilteredBookings: () => {
      const bookings = state.bookings.allIds.map(id => state.bookings.byId[id]);
      const { status, dateRange, vendor } = state.bookings.filters;

      return bookings.filter(booking => {
        if (status !== 'all' && booking.status !== status) return false;
        if (vendor && booking.vendor_id !== vendor) return false;
        // Add date range filtering logic here
        return true;
      });
    },

    getBookingById: (id) => state.bookings.byId[id],
    getBookingsLoading: () => state.bookings.loading,
    getBookingsPagination: () => state.bookings.pagination,

    // Vendor selectors
    getAllVendors: () => state.vendors.allIds.map(id => state.vendors.byId[id]),
    getVendorById: (id) => state.vendors.byId[id],
    getVendorsLoading: () => state.vendors.loading,

    // UI selectors
    getSidebarState: () => state.ui.sidebar,
    getTheme: () => state.ui.theme,
    getNotifications: () => state.ui.notifications,
    getModalState: () => state.ui.modal,
    getGlobalLoading: () => state.ui.loading.global,
    getComponentLoading: (component) => state.ui.loading.components[component] || false,
    getCurrentUser: () => state.users.currentUser,
    getUserPermissions: () => state.users.permissions,
    canAccessMenu: (menuId) => aclCanAccessMenu(state.users.currentUser, menuId),

    // Cache selectors
    getCachedData: (key) => {
      const cached = state.cache.data[key];
      if (!cached) return null;

      const isExpired = Date.now() - cached.timestamp > state.cache.ttl;
      return isExpired ? null : cached.data;
    },
  }), [state]);

  // 🎁 Context value with memoization (Performance optimization)
  const contextValue = useMemo(() => ({
    state,
    actions,
    selectors,
  }), [state, actions, selectors]);

  return (
    <AppContext.Provider value={contextValue}>
      {children}
    </AppContext.Provider>
  );
};

// 🎣 Custom Hook for using App Context
export const useAppContext = () => {
  const context = useContext(AppContext);

  if (!context) {
    throw new Error('useAppContext must be used within an AppProvider');
  }

  return context;
};

// 🎣 Specialized hooks for different parts of state
export const useDashboard = () => {
  const { state, actions, selectors } = useAppContext();

  return {
    stats: selectors.getDashboardStats(),
    setLoading: actions.setDashboardLoading,
    setStats: actions.setDashboardStats,
    setError: actions.setDashboardError,
  };
};

export const useBookings = () => {
  const { state, actions, selectors } = useAppContext();

  return {
    bookings: selectors.getFilteredBookings(),
    loading: selectors.getBookingsLoading(),
    pagination: selectors.getBookingsPagination(),
    filters: state.bookings.filters,
    setLoading: actions.setBookingsLoading,
    setBookings: actions.setBookings,
    addBooking: actions.addBooking,
    updateBooking: actions.updateBooking,
    deleteBooking: actions.deleteBooking,
    setFilters: actions.setBookingFilters,
    setPagination: actions.setBookingPagination,
  };
};

export const useVendors = () => {
  const { actions, selectors } = useAppContext();

  return {
    vendors: selectors.getAllVendors(),
    loading: selectors.getVendorsLoading(),
    setLoading: actions.setVendorsLoading,
    setVendors: actions.setVendors,
  };
};

export const useUI = () => {
  const { actions, selectors } = useAppContext();

  return {
    sidebar: selectors.getSidebarState(),
    theme: selectors.getTheme(),
    notifications: selectors.getNotifications(),
    modal: selectors.getModalState(),
    globalLoading: selectors.getGlobalLoading(),
    toggleSidebar: actions.toggleSidebar,
    setActiveMenu: actions.setActiveMenu,
    setTheme: actions.setTheme,
    addNotification: actions.addNotification,
    removeNotification: actions.removeNotification,
    markNotificationRead: actions.markNotificationRead,
    markAllNotificationsRead: actions.markAllNotificationsRead,
    clearNotifications: actions.clearNotifications,
    openModal: actions.openModal,
    closeModal: actions.closeModal,
    setGlobalLoading: actions.setGlobalLoading,
    getComponentLoading: selectors.getComponentLoading,
    setComponentLoading: actions.setComponentLoading,
    currentUser: selectors.getCurrentUser(),
    userPermissions: selectors.getUserPermissions(),
    setCurrentUser: actions.setCurrentUser,
    setUserPermissions: actions.setUserPermissions,
    canAccessMenu: selectors.canAccessMenu,
  };
};